import { useState } from "react";
import { handoffToFieldGuide } from "@/lib/api";
import { stripSurroundingQuotes } from "@/lib/text";
import { TESTIDS } from "@/constants/testIds";
import { ArrowRight, PenTool, Loader2, Sparkles, MapPin, ExternalLink, CheckCircle2 } from "lucide-react";

export default function FirstMove({ recommendation, analysisId, propertyCity }) {
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  if (!recommendation) return null;

  const onDraft = async () => {
    setBusy(true);
    setErr("");
    // Rotate stage copy while the (~30-60s) API call runs
    const stages = [
      "Finding walkable places near your property…",
      "Reviewing the queries you're missing…",
      "Drafting a starter note for each spot…",
      "Assembling your starter guide…",
      "Rendering the map and hero image…",
      "Almost ready…",
    ];
    let i = 0;
    setStage(stages[0]);
    const iv = setInterval(() => {
      i = (i + 1) % stages.length;
      setStage(stages[i]);
    }, 4500);
    try {
      const res = await handoffToFieldGuide(analysisId);
      if (res?.ok === false) {
        setErr(res.error_message || "Could not draft your starter guide. Please try again in a moment.");
      } else {
        setResult(res);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.error_message || "Could not draft your starter guide. Please try again in a moment.");
    } finally {
      clearInterval(iv);
      setBusy(false);
      setStage("");
    }
  };

  if (result) {
    return <BuiltState result={result} />;
  }

  return (
    <div
      className="glass rounded-3xl p-8 md:p-12 relative overflow-hidden"
      style={{
        borderColor: "rgba(107,122,85,0.35)",
        background:
          "linear-gradient(135deg, rgba(107,122,85,0.06) 0%, rgba(166,124,63,0.03) 100%)",
      }}
      data-testid={TESTIDS.firstMoveCard}
    >
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 mb-4">
            <PenTool className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
            <div className="label-eyebrow">Improve your Local Authority Score</div>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-6">
            We&apos;ve drafted a FieldGuide to help improve your Local Authority Score.
          </h2>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-lg mb-4">
            We&apos;ve called it{" "}
            {recommendation?.title ? (
              <span
                className="font-serif italic"
                style={{ color: "var(--text-primary)" }}
              >
                &ldquo;{recommendation.title}&rdquo;
              </span>
            ) : (
              <span className="font-serif italic" style={{ color: "var(--text-primary)" }}>
                a starter neighborhood guide
              </span>
            )}
            {" "}and we built it to address a key local-area opportunity for
            your hotel.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-3">
            But it&apos;s really important to note:{" "}
            <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
              THIS IS ONLY A DRAFT.
            </span>{" "}
            The next step is for you to fine-tune the FieldGuide &mdash; with
            insights only you and your team can provide.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-3">
            We&apos;ve suggested popular activities and restaurants &mdash; you
            should update these with your own descriptions, or replace them
            entirely.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-3">
            We&apos;ve structured the FieldGuide with AI criteria in mind. So
            when you add your local perspective, you&apos;ll have a much better
            chance of breaking through.
          </p>
          <p className="text-[color:var(--text-secondary)] leading-relaxed text-base mb-6">
            And when it comes to web-publishing, your FieldGuide takes care of
            the critical details: from brand-compatible fonts and colors, to
            mobile-first design, to ADA compliance and more. That means your
            content will work just as well for your on-property guests as it
            does for AI.
          </p>

          {err && (
            <div className="text-sm mb-3" style={{ color: "var(--score-low)" }}>
              {err}
            </div>
          )}

          <button
            className="btn-gold text-base md:text-lg !py-3.5 !px-6"
            onClick={onDraft}
            disabled={busy}
            data-testid={TESTIDS.firstMoveDraftBtn}
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Preparing your FieldGuide…
              </>
            ) : (
              <>
                Show Me My FieldGuide
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          {busy && stage && (
            <div className="mt-3 font-serif italic text-sm" style={{ color: "var(--brand-sage-deep)" }}>
              {stage}
            </div>
          )}
          {!busy && (
            <div className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
              Free to explore. No credit card until you publish.
            </div>
          )}
        </div>

        <div className="lg:col-span-4">
          <div className="glass rounded-2xl p-5" style={{ background: "var(--bg-card-warm)" }}>
            <div className="label-eyebrow mb-3">Informed by your report</div>
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed mb-4 italic font-serif">
              &ldquo;{recommendation.why_it_matters}&rdquo;
            </p>
            {recommendation.linked_query_texts?.length > 0 && (
              <>
                <div className="label-eyebrow mb-2">Targets these queries</div>
                <ul className="space-y-1.5">
                  {recommendation.linked_query_texts.slice(0, 4).map((q, i) => (
                    <li
                      key={i}
                      className="text-xs font-serif italic"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      &ldquo;{stripSurroundingQuotes(q)}&rdquo;
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BuiltState({ result }) {
  return (
    <div
      className="glass rounded-3xl p-8 md:p-12 relative overflow-hidden fade-in"
      style={{
        borderColor: "rgba(107,122,85,0.5)",
        background:
          "linear-gradient(135deg, rgba(107,122,85,0.09) 0%, rgba(166,124,63,0.05) 100%)",
      }}
    >
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-4 h-4" style={{ color: "var(--brand-sage-deep)" }} />
            <div className="label-eyebrow" style={{ color: "var(--brand-sage-deep)" }}>
              Your draft is ready
            </div>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
            Now make it yours.
          </h2>
          <p className="text-[color:var(--text-secondary)] leading-relaxed mb-6">
            Your property, account, and first guide are set up and open in the editor. Use the
            one-time link below to jump in, rewrite the notes in your voice, swap our picks for
            the ones you&apos;d actually send guests to, and add the firsthand details only your
            team knows — then publish when it feels like yours.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={result.magic_link_url}
              className="btn-gold text-base !py-3 !px-5"
              data-testid="handoff-magic-link"
            >
              <Sparkles className="w-4 h-4" />
              Edit the Guide
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={result.public_url}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-base !py-3 !px-5"
              data-testid="handoff-public-preview"
            >
              <ExternalLink className="w-4 h-4" />
              See the public preview
            </a>
          </div>
          {result.recommendations_skipped?.length > 0 && (
            <div className="mt-4 text-xs" style={{ color: "var(--text-muted)" }}>
              {result.recommendations_skipped.length} recommendation(s) were skipped (couldn&apos;t
              be geocoded). You can add replacements in the editor.
            </div>
          )}
          <div className="mt-4 text-xs italic" style={{ color: "var(--text-muted)" }}>
            Sign-in link is single-use and expires in 7 days.
          </div>
        </div>

        <div className="lg:col-span-5">
          {result.jpg_url && (
            <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--border-default)" }}>
              <img
                src={result.jpg_url}
                alt="Your guide hero"
                className="w-full h-auto block"
                data-testid="handoff-hero-image"
              />
            </div>
          )}
          <div className="mt-3 flex items-center gap-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
            <MapPin className="w-3 h-3" />
            AI-rendered hero, generated for your neighborhood
          </div>
        </div>
      </div>
    </div>
  );
}
